import { CHANGE_TYPES } from '../../constants';
import { compareItems } from '../utils';
import { sortPlanogramItems } from '../vending-utils';

// This method performs the initial mapping of planogram items
// so that they are group in sorted rows.
export const mapPlanogramToRows = planogramItems => {
	const planogramSplitIntoRows = planogramItems.reduce((acc, item) => {
		const rowIndex = item.row;

		// Make a row if it doesn't exist
		if (!acc[rowIndex]) {
			acc[rowIndex] = [];
		}

		acc[rowIndex].push(item);

		return acc;
	}, {});

	// Sort the rows in place
	const sortedRows = Object.keys(planogramSplitIntoRows).map(row => {
		return sortPlanogramItems(planogramSplitIntoRows[row]);
	});

	return sortedRows;
};

const toNonNegativeNumber = (value) => {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return 0;
	}

	return Math.max(0, numericValue);
};

const resolveExpiryDatesDisplayQuantity = (item) => {
	const pickedQuantity = toNonNegativeNumber(item?.pickedQuantity) + toNonNegativeNumber(item?.pickedExtraQuantity);
	if (item?.expiryDatesArePickedWarehouseStock) {
		return pickedQuantity;
	}

	return Math.max(pickedQuantity, toNonNegativeNumber(item?.quantity));
};

const appendExpiryDatesForBatch = ({
	currentExpiryDates = {},
	currentQuantity = 0,
	nextExpiryDates = {},
	nextQuantity = 0,
}) => {
	const mergedExpiryDates = { ...(currentExpiryDates || {}) };
	const resolvedCurrentQuantity = toNonNegativeNumber(currentQuantity);
	const resolvedNextQuantity = toNonNegativeNumber(nextQuantity);

	for (let selection = 1; selection <= resolvedNextQuantity; selection += 1) {
		mergedExpiryDates[resolvedCurrentQuantity + selection] = nextExpiryDates?.[selection] || null;
	}

	return {
		batchedExpiryDates: mergedExpiryDates,
		batchedExpiryDatesQuantity: resolvedCurrentQuantity + resolvedNextQuantity,
	};
};


export const batchSameProducts = (items, quantityFieldName, extraQuantityFieldName) => {

	const batchedInventory = items.reduce((acc, item) => {
		// Boolean check of whether product is already in the accumulator
		const productAlreadyInAcc = acc.some(accItem => {
			if (accItem.product && item.product) {
				return accItem.product.id === item.product.id;
			}

			return false;
		});

		// If it's not already in the accumulator, put it in
		if (!productAlreadyInAcc) {
			const itemExpiryDatesQuantity = resolveExpiryDatesDisplayQuantity(item);
			const {
				batchedExpiryDates,
				batchedExpiryDatesQuantity,
			} = appendExpiryDatesForBatch({
				currentExpiryDates: {},
				currentQuantity: 0,
				nextExpiryDates: item.expiryDates || {},
				nextQuantity: itemExpiryDatesQuantity,
			});

			// Spread new items, because otherwise it is immutable
			acc.push({
				...item,
				batchedSalesLast7Days: item.planogramItemStatistics?.salesLast7Days || 0,
				batchedSalesLast14Days: item.planogramItemStatistics?.salesLast14Days || 0,
				batchedSalesLast30Days: item.planogramItemStatistics?.salesLast30Days || 0,
				batchedSalesLast365Days: item.planogramItemStatistics?.salesLast365Days || 0,
				batchedLastProductChangeTime: item.planogramItemStatistics?.lastProductChangeTime,
				batchedPlanogramItemCreated: item.created,
				requiredQuantity: ((item[quantityFieldName] || 0) + (item[extraQuantityFieldName] || 0)) || (item.requiredQuantity || 0),
				selections: `${item.selection}`,
				batchedExpiryDates,
				batchedExpiryDatesQuantity,
			});
		}
		// Otherwise, update the existing pick list item with relevant details
		else {
			const updateItem = acc.find(accItem => {
				return accItem.product.id === item.product.id;
			});

			// Define the new selection field (for multiple selections)
			let newSelections = [];
			if (Array.isArray(updateItem.selection)) {
				// If selection is an array, you've already got more than 2 selections,
				// and just need to push new selection into the array
				newSelections = [...updateItem.selection];
				newSelections.push(item.selection);
			}
			else {
				// Otherwise, this is the first repeated product, so push both
				// values to newSelections
				newSelections.push(updateItem.selection);
				newSelections.push(item.selection);
			}

			// Define the new price field (removing duplicates)
			let newPrices = [];

			// Destructure prices for clarity
			const { price: itemPrice } = item;
			const { price: updateItemPrice } = updateItem;

			if (updateItemPrice === itemPrice) {
				// If the prices are the same, simply set the price as a string
				newPrices = itemPrice;
			}
			else {
				// Handle when prices are different
				if (Array.isArray(updateItemPrice)) {
					// If updateItemPrice is already an array, we've already done this once,
					// so just push to that array
					newPrices = updateItemPrice;
					newPrices.push(itemPrice);
				}
				else {
					// Otherwise, it's the first iteration of a new price, so push
					// both prices.
					newPrices.push(itemPrice);
					newPrices.push(updateItemPrice);
				}
			}

			// Define the new id field (for multiple ids)
			let newIds = [];
			if (Array.isArray(updateItem.id)) {
				// If id is an array, you've already got more than 2 ids,
				// and just need to push new id into the array
				newIds = [...updateItem.id];
				newIds.push(item.id);
			}
			else {
				// Otherwise, this is the first repeated product, so push both
				// values to newIds
				newIds.push(updateItem.id);
				newIds.push(item.id);
			}

			// Process queued price; it's tricky as we don't want to include prices queued on "next empty"
			const { queuedPrice, queuedPriceType } = item;
			const { queuedPrice: updateItemQueuedPrice, queuedPriceType: updateItemQueuedPriceType } = updateItem;

			// Define the new price field (removing duplicates)
			let newQueuedPrices = [];

			// process only if queued on next service
			if (queuedPriceType === CHANGE_TYPES.NEXT_SERVICE) {
				// add price to previous values only if previous also queued on "next service" and prices are different
				if (updateItemQueuedPriceType === CHANGE_TYPES.NEXT_SERVICE && queuedPrice !== updateItemQueuedPrice) {
					if (Array.isArray(updateItemQueuedPrice)) {
						// If updateItemPrice is already an array, we've already done this once,
						// so just push to that array
						newQueuedPrices = updateItemQueuedPrice;
						newQueuedPrices.push(queuedPrice);
					}
					else {
						// Otherwise, it's the first iteration of a new price, so push
						// both prices.
						newQueuedPrices.push(queuedPrice);
						newQueuedPrices.push(updateItemQueuedPrice);
					}
				}
				// otherwise override the previous values
				else {
					newQueuedPrices = queuedPrice;
				}
			}

			// keep "next service" if previously set
			const newQueuedPriceType = [queuedPriceType, updateItemQueuedPriceType].includes(CHANGE_TYPES.NEXT_SERVICE) ? CHANGE_TYPES.NEXT_SERVICE : null;
			const itemExpiryDatesQuantity = resolveExpiryDatesDisplayQuantity(item);
			const {
				batchedExpiryDates,
				batchedExpiryDatesQuantity,
			} = appendExpiryDatesForBatch({
				currentExpiryDates: updateItem.batchedExpiryDates || {},
				currentQuantity: updateItem.batchedExpiryDatesQuantity || 0,
				nextExpiryDates: item.expiryDates || {},
				nextQuantity: itemExpiryDatesQuantity,
			});

			// Set the updated item

			updateItem.id = newIds;
			updateItem.selection = newSelections;
			updateItem.quantity = item.quantity + updateItem.quantity;
			updateItem.extraQuantity = item.extraQuantity + updateItem.extraQuantity;
			updateItem.parlevel = item.parlevel + updateItem.parlevel;
			updateItem.capacity = item.capacity + updateItem.capacity;
			updateItem.price = newPrices;
			updateItem.queuedPrice = newQueuedPrices;
			updateItem.queuedPriceType = newQueuedPriceType;
			updateItem.requiredQuantity += ((item[quantityFieldName] || 0) + (item[extraQuantityFieldName] || 0)) || (item.requiredQuantity || 0);
			updateItem.pickedQuantity += item.pickedQuantity;
			updateItem.pickedExtraQuantity += item.pickedExtraQuantity;
			updateItem.isPicked = updateItem.isPicked || item.isPicked; // if any item isPicked disable change quantity

			updateItem.batchedSalesLast7Days += (item.planogramItemStatistics?.salesLast7Days || 0);
			updateItem.batchedSalesLast14Days += (item.planogramItemStatistics?.salesLast14Days || 0);
			updateItem.batchedSalesLast30Days += (item.planogramItemStatistics?.salesLast30Days || 0);
			updateItem.batchedSalesLast365Days += (item.planogramItemStatistics?.salesLast365Days || 0);
			updateItem.batchedExpiryDates = batchedExpiryDates;
			updateItem.batchedExpiryDatesQuantity = batchedExpiryDatesQuantity;

			// get latest change date
			if (!updateItem.batchedLastProductChangeTime && item.planogramItemStatistics?.lastProductChangeTime ||
				updateItem.batchedLastProductChangeTime > item.planogramItemStatistics?.lastProductChangeTime) {
				updateItem.batchedLastProductChangeTime = item.planogramItemStatistics?.lastProductChangeTime;
			}

			// get latest create date
			if (!updateItem.batchedPlanogramItemCreated || updateItem.planogramItemCreated > item.created) {
				updateItem.batchedPlanogramItemCreated = item.created;
			}

			updateItem.selections = `${updateItem.selections}, ${item.selection}`;

			// DELETE any removed product, as it will be unweildy to show
			// all removed products when batching items. No need to do it when adding to
			// the accumulator - only on the second pass - as removedProduct can be included
			// when there is only one product.
			delete updateItem.removedProduct;
		}
		return acc;
	}, []);

	return batchedInventory;
};

export const shouldDecrementQuantityOnSale = planogramItem => (planogramItem.decrementQuantityOnSale === null || planogramItem.decrementQuantityOnSale === undefined
	? Boolean(planogramItem.product && planogramItem.product.decrementQuantityOnSale)
	: planogramItem.decrementQuantityOnSale);

const PLANOGRAM_POSITION_GROUP_MULTIPLIER = 1000000000;
const PLANOGRAM_POSITION_GROUP_TIE_MULTIPLIER = 1000000;
// Coordinate positions must stay below the missing-coordinate offset, and all item positions below the tie multiplier.
const PLANOGRAM_MISSING_COORDINATE_POSITION_OFFSET = 500000;

const getIdString = (value) => {
	if (!value) {
		return '';
	}
	if (typeof value === 'string') {
		return value;
	}
	if (value._id) {
		return value._id.toString();
	}
	if (value.id) {
		return value.id.toString();
	}
	return value.toString();
};

const getPlanogramItemGroupId = group => getIdString(group?.id || group?._id);

const hasPlanogramItemGroupOrderValue = value => (
	value !== null &&
	value !== undefined &&
	value !== ''
);

const getNormalizedPlanogramItemGroupOrder = (group, fallbackOrder = 0) => {
	if (!hasPlanogramItemGroupOrderValue(group?.order)) {
		return fallbackOrder;
	}
	const order = Number(group?.order);
	return Number.isFinite(order) ? order : fallbackOrder;
};

const comparePlanogramItemGroups = (groupA, groupB, fallbackOrder = 0) => {
	const orderA = getNormalizedPlanogramItemGroupOrder(groupA, fallbackOrder);
	const orderB = getNormalizedPlanogramItemGroupOrder(groupB, fallbackOrder);
	if (orderA !== orderB) {
		return orderA - orderB;
	}

	const nameComparison = String(groupA?.name || '').localeCompare(String(groupB?.name || ''));
	if (nameComparison) {
		return nameComparison;
	}

	return getPlanogramItemGroupId(groupA).localeCompare(getPlanogramItemGroupId(groupB));
};

export const comparePlanogramItemGroupsByPosition = (groupA, groupB) => comparePlanogramItemGroups(groupA, groupB);

export const comparePlanogramItemGroupsForDisplay = (groupA, groupB) => (
	comparePlanogramItemGroups(groupA, groupB, Number.MAX_SAFE_INTEGER)
);

const buildPlanogramItemGroupPositions = (planogramItemGroups = []) => {
	const duplicateOrderRanks = {};

	return [...(planogramItemGroups || [])]
		.sort(comparePlanogramItemGroupsByPosition)
		.reduce((acc, group) => {
			const groupId = getPlanogramItemGroupId(group);
			if (!groupId) {
				return acc;
			}

			const groupOrder = getNormalizedPlanogramItemGroupOrder(group);
			const groupOrderKey = String(groupOrder);
			const duplicateOrderRank = duplicateOrderRanks[groupOrderKey] || 0;
			duplicateOrderRanks[groupOrderKey] = duplicateOrderRank + 1;

			acc[groupId] = PLANOGRAM_POSITION_GROUP_MULTIPLIER * groupOrder +
				PLANOGRAM_POSITION_GROUP_TIE_MULTIPLIER * duplicateOrderRank;
			return acc;
		}, {});
};

const hasPlanogramCoordinateValue = value => (
	value !== null &&
	value !== undefined &&
	value !== '' &&
	Number.isFinite(Number(value))
);

const getPlanogramItemGroupKey = planogramItem => getIdString(planogramItem?.planogramItemGroupId || planogramItem?.planogramItemGroup);

const getPlanogramItemSetupTime = (planogramItem) => {
	const setupTime = new Date(planogramItem?.created).getTime();
	return Number.isNaN(setupTime) ? Number.POSITIVE_INFINITY : setupTime;
};

const comparePlanogramItemsBySetupFallbackOrder = (itemA, itemB) => {
	const setupTimeDifference = getPlanogramItemSetupTime(itemA.planogramItem) - getPlanogramItemSetupTime(itemB.planogramItem);
	if (setupTimeDifference) {
		return setupTimeDifference;
	}

	const idComparison = getIdString(itemA.planogramItem?.id || itemA.planogramItem?._id)
		.localeCompare(getIdString(itemB.planogramItem?.id || itemB.planogramItem?._id));
	if (idComparison) {
		return idComparison;
	}

	return itemA.itemIndex - itemB.itemIndex;
};

const buildMissingCoordinateFallbackPositions = (items, planogramItemField) => {
	const itemsByGroup = items.reduce((acc, item, itemIndex) => {
		const planogramItem = planogramItemField ? item[planogramItemField] : item;
		const groupKey = getPlanogramItemGroupKey(planogramItem);
		if (!acc[groupKey]) {
			acc[groupKey] = [];
		}
		acc[groupKey].push({ itemIndex, planogramItem });
		return acc;
	}, {});

	return Object.values(itemsByGroup).reduce((acc, groupItems) => {
		[...groupItems]
			.sort(comparePlanogramItemsBySetupFallbackOrder)
			.forEach((item, setupIndex) => {
				acc[item.itemIndex] = PLANOGRAM_MISSING_COORDINATE_POSITION_OFFSET + setupIndex;
			});
		return acc;
	}, {});
};

// items can be pick list items or planogram items
export const calculatePlanogramItemsPositions = ({
	items, planogramItemGroups, planogramItemField, sortDirection,
}) => {
	const planogramItemGroupPositions = buildPlanogramItemGroupPositions(planogramItemGroups);
	const missingCoordinateFallbackPositions = buildMissingCoordinateFallbackPositions(items, planogramItemField);

	// iterate the items again to find the % position in the whole planogram
	const clonedItems = items.map((item, itemIndex) => {
		// resolve basic item stuff again
		const planogramItem = planogramItemField ? item[planogramItemField] : item;
		const groupKey = getPlanogramItemGroupKey(planogramItem);
		const hasCoordinates = hasPlanogramCoordinateValue(planogramItem.row) && hasPlanogramCoordinateValue(planogramItem.column);

		// get group order
		const planogramItemGroupPosition = planogramItemGroupPositions[groupKey] || 0;
		const planogramItemPosition = hasCoordinates
			? 100 * Number(planogramItem.row) + Number(planogramItem.column)
			: missingCoordinateFallbackPositions[itemIndex];

		if (planogramItemField) {
			return {
				...item,
				[planogramItemField]: {
					...planogramItem,
					position: planogramItemGroupPosition + // group position is the "strongest"
						planogramItemPosition, // then row/column, or setup order after coordinate rows when coordinates are missing
				},
			};
		}

		return {
			...item,
			position: planogramItemGroupPosition + // group position is the "strongest"
				planogramItemPosition, // then row/column, or setup order after coordinate rows when coordinates are missing
		};
	});

	// if no sort then just return items
	if (!sortDirection) {
		return clonedItems;
	}

	// and finally sort by position and return
	return [...clonedItems].sort(compareItems(null, sortDirection, item => {
		const planogramItem = planogramItemField ? item[planogramItemField] : item;
		return planogramItem?.position;
	}));
};

const toTagIdString = value => {
	if (!value) {
		return null;
	}

	if (typeof value === 'string') {
		return value;
	}

	if (value._id) {
		return value._id.toString();
	}

	if (value.id) {
		return value.id.toString();
	}

	if (typeof value.toString === 'function') {
		return value.toString();
	}

	return null;
};

export const resolveRestrictedProductTags = machine => {
	if (!machine) {
		return;
	}

	if (machine.overrideRestrictedProductTags) {
		return machine.restrictedProductTags;
	}

	return machine.location?.restrictedProductTags;
};

export const productHasRestrictedTags = ({ product, restrictedProductTags }) => {
	if (!product?.tags?.length || !restrictedProductTags?.length) {
		return false;
	}

	const restrictedTagIds = new Set(restrictedProductTags.map(toTagIdString).filter(Boolean));

	return product.tags.some(tag => restrictedTagIds.has(toTagIdString(tag)));
};

export const isProductRestrictedForMachine = ({ product, machine }) => productHasRestrictedTags({
	product,
	restrictedProductTags: resolveRestrictedProductTags(machine),
});

export const getDifferentProductWithTheSameBarcode = (planogramItems) => {

	// {
	// 	barecode: [uniqueProductName1, uniqueProductName2],
	// 	barecode: [uniqueProductName1, uniqueProductName2],
	// };
	const duplicatedBarecodesObject = {};
	const productsWithoutBarecodeList = [];
	planogramItems?.forEach(planogramItem => {
		// skip empty planogramItems
		if (!planogramItem.product) {
			return;
		}

		if (!planogramItem.product.barcodes?.length && !productsWithoutBarecodeList.includes(planogramItem.product.name)) {
			productsWithoutBarecodeList.push(planogramItem.product.name);
		}

		planogramItem.product.barcodes?.forEach(barecode => {
			if (!duplicatedBarecodesObject[barecode]) {
				duplicatedBarecodesObject[barecode] = [];
			}

			if (!duplicatedBarecodesObject[barecode].includes(planogramItem.product.name)) {
				duplicatedBarecodesObject[barecode].push(planogramItem.product.name);
			}
		});
	});
	// remove items which has no duplicated barecodes
	const filteredDuplicatedBarecodesObject = Object.entries(duplicatedBarecodesObject).reduce((acc, [key, value]) => {
		if (value.length > 1) {
			acc[key] = value;
		}
		return acc;
	}, {});

	return [filteredDuplicatedBarecodesObject, productsWithoutBarecodeList];
};
